import React from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import { getFlowPhoto, getFlowDetails, getFlowImageB64, setFlowDraft } from './flowStore';
import { pickDraftRoute, analyzeErrorMessage } from './inputMode';

const STEPS = ['Finding foods', 'Matching nutrition', 'Preparing your review'];

// Module-level guard: ensures the analyze request fires ONCE even under StrictMode double-mount,
// and is shared across mounts so navigation still happens on the surviving mount.
let _inflight = null;

// Step 2 — Analyzing. Supports all three modes: photo -> /ai-log/draft/image, description-only ->
// /ai-log/draft/text, photo+description -> /ai-log/draft/image (the description is sent along).
// Read-only (no DB write until the commit step). We only fall back to the start screen when BOTH the
// photo and the description are missing — a written description alone is a valid submission.
export default function AILogAnalyzing() {
  const navigate = useNavigate();
  const photo = getFlowPhoto();
  const [done, setDone] = React.useState(0);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    let alive = true;
    const imageB64 = getFlowImageB64();
    const notes = (getFlowDetails() || '').trim();

    // Fail safe ONLY when there is neither a photo nor a description — never fabricate a draft.
    if (!imageB64 && !notes) {
      navigate('/ai-log', { replace: true });
      return () => { alive = false; };
    }

    const timers = [];
    STEPS.slice(0, -1).forEach((_, i) => timers.push(setTimeout(() => alive && setDone(i + 1), 900 * (i + 1))));

    // Ask the server whether this user is eligible for the rebuild-v2 draft, THEN pick the route.
    // A rebuild request, once selected, NEVER silently falls back to legacy if it errors. And a FAILURE to
    // determine routing (availability request itself failing) must fail VISIBLY — never assume legacy.
    if (!_inflight) {
      _inflight = api.get('/ai-log/availability')
        .then((r) => {
          const rebuildV2 = Boolean(r && r.data && r.data.rebuild_v2_available);
          const route = pickDraftRoute(imageB64, notes, rebuildV2);
          return api.post(route.path, route.body).then((resp) => ({ resp, route }));
        });
    }

    _inflight
      .then(({ resp }) => {
        setFlowDraft((resp.data && resp.data.draft) || null);
        if (alive) setDone(STEPS.length);
        setTimeout(() => navigate('/ai-log/review', { replace: true }), 300);
      })
      .catch((e) => {
        if (!alive) return;
        const status = e && e.response && e.response.status;
        const detail = e && e.response && e.response.data && e.response.data.detail;
        setErr(analyzeErrorMessage(status, detail));
      })
      .finally(() => { _inflight = null; });

    return () => { alive = false; timers.forEach(clearTimeout); };
  }, [navigate]);

  if (err) {
    return (
      <div className="bg-background flex flex-col items-center justify-center px-8 text-center" style={{ minHeight: '100dvh', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="ai-log-analyzing-error">
        <span className="size-14 rounded-full bg-destructive/15 flex items-center justify-center"><AlertCircle size={26} className="text-destructive" /></span>
        <h1 className="text-lg font-bold text-foreground mt-4">Something went wrong</h1>
        <p className="text-sm text-muted-foreground mt-1" data-testid="analyze-error-msg">{err}</p>
        <div className="flex gap-3 mt-6">
          <button onClick={() => navigate('/ai-log', { replace: true })} className="h-11 px-5 rounded-xl border border-border/50 text-sm font-medium" data-testid="analyze-error-back">Start over</button>
          <button onClick={() => window.location.reload()} className="h-11 px-5 rounded-xl bg-accent text-white text-sm font-semibold" data-testid="analyze-error-retry">Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background flex flex-col items-center justify-center px-8" style={{ minHeight: '100dvh', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="ai-log-analyzing">
      {photo ? (
        <div className="relative">
          <img src={photo} alt="Your meal" className="size-28 rounded-2xl object-cover" />
          <div className="absolute inset-0 rounded-2xl ring-2 ring-accent/40 animate-pulse" />
        </div>
      ) : (
        <Loader2 size={40} className="text-accent animate-spin" />
      )}
      <h1 className="text-lg font-bold text-foreground mt-6">Analyzing your meal…</h1>
      <p className="text-sm text-muted-foreground mt-1">This just takes a moment.</p>

      <div className="w-full max-w-xs mt-6 space-y-3">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-3" data-testid={'analyze-step-' + i}>
            {done > i ? (
              <span className="size-6 rounded-full bg-accent flex items-center justify-center"><Check size={14} className="text-white" /></span>
            ) : done === i ? (
              <span className="size-6 rounded-full bg-accent/15 flex items-center justify-center"><Loader2 size={14} className="text-accent animate-spin" /></span>
            ) : (
              <span className="size-6 rounded-full bg-card border border-border/40" />
            )}
            <span className={'text-sm ' + (done > i ? 'text-foreground font-medium' : done === i ? 'text-foreground' : 'text-muted-foreground')}>{s}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
